import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MonoTypeOperatorFunction, filter } from 'rxjs';
import {
  AgentConnectionState,
  AgentMessageDto,
  AgentTurnContext,
  ConfirmationRequiredPayload,
  InputRequestedPayload,
  SessionEvent,
} from 'app/shared/models/agent.model';
import { AgentSignalRService } from 'app/shared/services/agent-signalr.service';
import { TranslocoService } from '@jsverse/transloco';

export type ConversationEntryKind =
  | 'user'
  | 'assistant'
  | 'tool-activity'
  | 'tool-call'
  | 'tool-result'
  | 'error'
  | 'system'
  | 'context';

export interface ConversationEntry {
  /** Stable id for `@for` tracking. */
  id: number;
  kind: ConversationEntryKind;
  /** For context entries: the group, page and time, ready to show. */
  text: string;
  toolName?: string;
  toolCallId?: string;
  /** For tool-call entries: the proposed payload, rendered in a confirmation card. */
  payloadJson?: string;
  /**
   * Present only on assistant entries created during live streaming (not history).
   * Each element is one token exactly as received from the hub. The bubble component
   * renders these as individual `<span>` elements so each new token animates in;
   * existing spans stay in the DOM untouched.
   */
  tokens?: string[];
}

/**
 * Signal-based conversation state for a single agent chat surface, wrapping the
 * shared {@link AgentSignalRService} transport. This is the reusable "brain"
 * lifted out of the admin tester so multiple chat UIs (the admin tester, the
 * Story feature) can share the streaming / rehydration / human-in-the-loop
 * gating logic.
 *
 * Provide it at the component level (`providers: [AgentConversationStore]`) so
 * each surface owns one conversation. The underlying SignalR connection stays
 * shared via the root-provided {@link AgentSignalRService}.
 *
 * The store deliberately holds NO presentation concerns (scrolling, logging) —
 * consumers react to the exposed signals.
 */
@Injectable()
export class AgentConversationStore {
  private readonly agent = inject(AgentSignalRService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);

  private readonly _connectionState = signal<AgentConnectionState>('disconnected');
  private readonly _messages = signal<ConversationEntry[]>([]);
  private readonly _busy = signal(false);
  private readonly _sessionId = signal<number | null>(null);
  private readonly _isResumed = signal(false);
  private readonly _turnCount = signal(0);
  private readonly _pendingConfirmation = signal<ConfirmationRequiredPayload | null>(null);
  private readonly _pendingInput = signal<InputRequestedPayload | null>(null);
  private readonly _inputValidationError = signal<string | null>(null);
  private readonly _sessionCompletedSummary = signal<string | null>(null);
  private readonly _error = signal<string | null>(null);

  /** Connection lifecycle of the shared hub. */
  readonly connectionState = this._connectionState.asReadonly();
  /** Ordered conversation entries (chat bubbles + tool/system markers). */
  readonly messages = this._messages.asReadonly();
  /** True while a turn is streaming and input should be disabled. */
  readonly busy = this._busy.asReadonly();
  readonly sessionId = this._sessionId.asReadonly();
  readonly isResumed = this._isResumed.asReadonly();
  readonly turnCount = this._turnCount.asReadonly();
  /** A tool call awaiting the user's approve/reject. */
  readonly pendingConfirmation = this._pendingConfirmation.asReadonly();
  /** A field value the agent is asking the user to provide. */
  readonly pendingInput = this._pendingInput.asReadonly();
  readonly inputValidationError = this._inputValidationError.asReadonly();
  /** Set once the session completes; carries the agent's closing summary. */
  readonly sessionCompletedSummary = this._sessionCompletedSummary.asReadonly();
  /** Last error message from a hub invoke or a SessionError event. */
  readonly error = this._error.asReadonly();

  readonly isConnected = computed(() => this._connectionState() === 'connected');
  readonly isWaitingForUser = computed(
    () => this._pendingConfirmation() !== null || this._pendingInput() !== null,
  );
  readonly canSend = computed(
    () =>
      this.isConnected() &&
      this._sessionId() !== null &&
      !this._busy() &&
      this._pendingConfirmation() === null,
  );

  /** Index of the in-progress streaming assistant entry, or null when none. */
  private streamingIndex: number | null = null;
  private nextId = 0;
  // The input the user just answered. Set for the whole hub round-trip so a
  // second click can't re-submit the same toolCallId; inputValidationFailed$
  // restores the widget from it and turnComplete$ reads it to push the
  // tool-result entry.
  private pendingInputInFlight: InputRequestedPayload | null = null;

  constructor() {
    this.subscribeToHub();
  }

  // --- intent methods ---

  /** Open the shared hub connection. Idempotent. */
  async connect(): Promise<void> {
    try {
      await this.agent.connect();
    } catch (err) {
      this._error.set(this.errorString(err));
    }
  }

  /** Close the shared hub connection and reset this conversation. */
  async disconnect(): Promise<void> {
    try {
      await this.agent.disconnect();
    } finally {
      this.resetSession();
    }
  }

  /**
   * Start or resume a session. An `openingMessage` (text the user typed before
   * the chat opened) becomes the first turn instead of the agent's greeting.
   */
  async startSession(agentKey: string, groupId: number | null, openingMessage?: string): Promise<void> {
    if (!agentKey) return;
    try {
      const result = await this.agent.startSession(agentKey, groupId);
      this._sessionId.set(result.sessionId);
      this._isResumed.set(result.isResumed);
      this.renderHistory(result.messages ?? []);

      // A resumed session may have restored an unresolved confirmation or input
      // request. The message is sent anyway: the backend closes open calls
      // itself (rejected / cancelled_by_user) and the model reads the message.
      // sendMessage clears a pending input; the confirmation is cleared here.
      const message = openingMessage?.trim();
      if (message) {
        this._pendingConfirmation.set(null);
        void this.sendMessage(message);
        return;
      }

      // The hub flags whether the agent should produce an opening turn. We don't
      // await it — tokens stream through the same receiveToken$ pipeline as a
      // normal user message. The startAssistantEntry()/busy pair mirrors
      // sendMessage so input stays disabled while the greeting streams.
      if (result.shouldOpenConversation) {
        this.startAssistantEntry();
        this._busy.set(true);
        this.agent.beginConversation(result.sessionId).catch((err) => {
          this._busy.set(false);
          this._error.set(this.errorString(err));
        });
      }
    } catch (err) {
      this._error.set(this.errorString(err));
    }
  }

  async sendMessage(message: string, context: AgentTurnContext | null = null): Promise<void> {
    const msg = message.trim();
    const sid = this._sessionId();
    if (!msg || sid === null) return;

    // Free text supersedes any open input request. The backend cancels the
    // pending call itself (cancelled_by_user) and sends the message in the
    // same turn, so the model can read the answer out of the message.
    // Do NOT skip it here — a skip tells the model the user declined.
    this.pendingInputInFlight = null;
    this._pendingInput.set(null);
    this._inputValidationError.set(null);
    // Same for an open confirmation: the backend rejects it (rejected_by_user). Left in place it
    // would keep every later turn busy, since TurnComplete only clears busy without one.
    this._pendingConfirmation.set(null);

    this.push({ kind: 'user', text: msg });
    this.startAssistantEntry();
    this._busy.set(true);
    try {
      await this.agent.sendMessage(sid, msg, context);
    } catch (err) {
      this._busy.set(false);
      this._error.set(this.errorString(err));
    }
  }

  async approve(): Promise<void> {
    const sid = this._sessionId();
    const pending = this._pendingConfirmation();
    if (sid === null || !pending) return;
    this._pendingConfirmation.set(null);
    this.push({
      kind: 'tool-result',
      text: `Result for ${pending.toolName}`,
      toolName: pending.toolName,
      toolCallId: pending.toolCallId,
    });
    this.startAssistantEntry();
    this._busy.set(true);
    try {
      await this.agent.confirmProposal(sid, pending.toolCallId);
    } catch (err) {
      this._busy.set(false);
      this._error.set(this.errorString(err));
    }
  }

  async reject(feedback?: string): Promise<void> {
    const sid = this._sessionId();
    const pending = this._pendingConfirmation();
    if (sid === null || !pending) return;
    this._pendingConfirmation.set(null);
    this.push({
      kind: 'tool-result',
      text: `Result for ${pending.toolName} (rejected)`,
      toolName: pending.toolName,
      toolCallId: pending.toolCallId,
    });
    this.startAssistantEntry();
    this._busy.set(true);
    try {
      await this.agent.rejectProposal(sid, pending.toolCallId, feedback ?? '');
    } catch (err) {
      this._busy.set(false);
      this._error.set(this.errorString(err));
    }
  }


  async submitInput(value: string): Promise<void> {
    const sid = this._sessionId();
    const pending = this._pendingInput();
    if (sid === null || !pending) return;
    // Re-entrancy guard: a second click can land before change detection
    // unmounts the widget, and the backend has already resolved the call.
    if (this.pendingInputInFlight) return;
    this._inputValidationError.set(null);

    // Translate the display text for the user bubble. The value sent to the
    // backend stays canonical (Enum key, "true"/"false", raw text) — only the
    // display representation is localized.
    let displayText = value;
    if (pending.dataType === 'Enum') {
      const selectedOption = pending.options.find((opt) => opt.key === value);
      if (selectedOption) {
        displayText = selectedOption.label;
      }
    } else if (pending.dataType === 'Bool') {
      displayText = this.transloco.translate(
        value === 'true' ? 'INSIGHT.GLOBAL.YES' : 'INSIGHT.GLOBAL.NO'
      );
    }

    // Unmount the widget immediately so it can't be clicked twice. Keep the
    // payload in pendingInputInFlight: inputValidationFailed$ restores the
    // widget from it, and turnComplete$ reads it to push the tool-result entry.
    this.pendingInputInFlight = pending;
    this._pendingInput.set(null);

    // The widget unmounts once answered, so write the question into the thread
    // as the permanent record of what was asked. Matches what renderHistory
    // produces for resolved RequestFieldValue calls on resume.
    if (pending.question?.trim()) {
      this.push({ kind: 'assistant', text: pending.question });
    }
    this.push({ kind: 'user', text: displayText });
    this.startAssistantEntry();
    this._busy.set(true);
    try {
      await this.agent.submitInput(sid, pending.toolCallId, value, false);
    } catch (err) {
      // Nothing reached the backend, so put the widget back and release the
      // guard — otherwise the answer is unrecoverable except via free text.
      this.pendingInputInFlight = null;
      this._pendingInput.set(pending);
      this._busy.set(false);
      this._error.set(this.errorString(err));
    }
  }

  async skipInput(): Promise<void> {
    const sid = this._sessionId();
    const pending = this._pendingInput();
    if (sid === null || !pending) return;
    if (this.pendingInputInFlight) return;
    this.pendingInputInFlight = pending;
    this._pendingInput.set(null);
    this._inputValidationError.set(null);
    if (pending.question?.trim()) {
      this.push({ kind: 'assistant', text: pending.question });
    }
    this.push({ kind: 'user', text: this.transloco.translate('INSIGHT.GLOBAL.SKIPPED') });
    this.startAssistantEntry();
    this._busy.set(true);
    try {
      await this.agent.submitInput(sid, pending.toolCallId, null, true);
    } catch (err) {
      // Nothing reached the backend, so put the widget back and release the
      // guard — otherwise the answer is unrecoverable except via free text.
      this.pendingInputInFlight = null;
      this._pendingInput.set(pending);
      this._busy.set(false);
      this._error.set(this.errorString(err));
    }
  }

  async stop(): Promise<void> {
    const sid = this._sessionId();
    if (sid === null) return;
    try {
      await this.agent.stopTurn(sid);
    } catch (err) {
      this._error.set(this.errorString(err));
    }
  }

  async cancel(): Promise<void> {
    const sid = this._sessionId();
    if (sid === null) return;
    try {
      await this.agent.cancelSession(sid);
      this.resetSession();
    } catch (err) {
      this._error.set(this.errorString(err));
    }
  }

  /**
   * Starts over in the same session: the server moves the context boundary so the model forgets
   * the earlier messages, and the thread is cleared. The session itself stays open.
   */
  async resetConversation(): Promise<void> {
    const sid = this._sessionId();
    if (sid === null || this._busy()) return;
    try {
      await this.agent.resetConversation(sid);
      this._messages.set([]);
      this._turnCount.set(0);
      this._pendingConfirmation.set(null);
      this.pendingInputInFlight = null;
      this._pendingInput.set(null);
      this._inputValidationError.set(null);
      this._error.set(null);
      this.streamingIndex = null;
    } catch (err) {
      this._error.set(this.errorString(err));
    }
  }

  clearError(): void {
    this._error.set(null);
  }

  resetSession(): void {
    this._sessionId.set(null);
    this._isResumed.set(false);
    this._turnCount.set(0);
    this._messages.set([]);
    this._pendingConfirmation.set(null);
    this.pendingInputInFlight = null;
    this._pendingInput.set(null);
    this._inputValidationError.set(null);
    this._sessionCompletedSummary.set(null);
    this._error.set(null);
    this.streamingIndex = null;
    this._busy.set(false);
  }

  // --- hub event handling ---

  private subscribeToHub(): void {
    const ref = this.destroyRef;

    this.agent.connectionState$
      .pipe(takeUntilDestroyed(ref))
      .subscribe((state) => this._connectionState.set(state));

    this.agent.receiveToken$.pipe(this.forThisSession(), takeUntilDestroyed(ref))
      .subscribe((evt) => this.appendToken(evt.token));

    // The server saved a new context for the question just sent: show it above that question.
    this.agent.contextChanged$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      const messages = this._messages();
      const question = messages.map((m) => m.kind).lastIndexOf('user');
      const answered = messages.slice(question + 1).some((m) => m.kind !== 'assistant' || !!m.text || !!m.tokens?.length);
      const at = question === -1 || answered ? messages.length : question;
      this._messages.set([...messages.slice(0, at), this.contextEntry(evt.groupName, evt.pageName, evt.createdAt), ...messages.slice(at)]);
      if (this.streamingIndex !== null && at <= this.streamingIndex) this.streamingIndex++;
    });

    this.agent.receiveToolActivity$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.finalizeAssistantEntry();
      this.push({
        kind: 'tool-activity',
        text: `${evt.toolName}: ${evt.description}`,
        toolName: evt.toolName,
      });
    });

    this.agent.confirmationRequired$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.finalizeAssistantEntry();
      this.push({
        kind: 'tool-call',
        text: `Tool call: ${evt.toolName}`,
        toolName: evt.toolName,
        toolCallId: evt.toolCallId,
        payloadJson: evt.proposedPayloadJson,
      });
      this._pendingConfirmation.set(evt);
      this._busy.set(false); // hub paused; user must approve/reject
    });

    this.agent.turnComplete$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this._turnCount.set(evt.turnNumber);
      this.finalizeAssistantEntry();
      const answered = this.pendingInputInFlight ?? this._pendingInput();
      if (answered) {
        this.push({
          kind: 'tool-result',
          text: 'Result for RequestFieldValue',
          toolName: 'RequestFieldValue',
          toolCallId: answered.toolCallId,
        });
      }
      this.pendingInputInFlight = null;
      this._pendingInput.set(null);
      this._inputValidationError.set(null);
      // In multi-turn auto-tool round-trips TurnComplete fires several times for
      // one user message; we re-enable input each time unless a confirmation is
      // still pending.
      if (!this._pendingConfirmation()) {
        this._busy.set(false);
      }
    });

    // The server threw away the turn it was streaming (the model wrote a tool call
    // as text) and is retrying. Drop the partial bubble; stay busy — the retry's
    // TurnComplete (or a SessionError) clears it.
    this.agent.turnDiscarded$.pipe(this.forThisSession(), takeUntilDestroyed(ref))
      .subscribe(() => this.discardAssistantEntry());

    // The model failed before answering and the server undid the user's action (their
    // message, or their answer to a question) to keep the session usable. Show the
    // conversation as the server now has it — an open question comes back as its widget.
    // The SessionError that follows shows the error and clears busy.
    this.agent.turnRolledBack$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.pendingInputInFlight = null;
      this.renderHistory(evt.messages);
    });

    this.agent.sessionCompleted$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.push({ kind: 'system', text: `Session completed: ${evt.summary}` });
      this._sessionCompletedSummary.set(evt.summary);
      this._busy.set(false);
    });

    this.agent.sessionError$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.finalizeAssistantEntry();
      this.push({ kind: 'error', text: `Error (${evt.code}): ${evt.message}` });
      this._error.set(evt.message);
      this._busy.set(false);
    });

    this.agent.inputRequested$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      this.finalizeAssistantEntry();
      // The widget renders the question itself. If the model also wrote it into
      // its message, strip it so the user doesn't see it twice.
      this.stripTrailingQuestion(evt.question);
      this.pendingInputInFlight = null;
      this._pendingInput.set(evt);
      this._inputValidationError.set(null);
      this._busy.set(false);
    });

    this.agent.inputValidationFailed$.pipe(this.forThisSession(), takeUntilDestroyed(ref)).subscribe((evt) => {
      // Restore the widget so the user can correct their answer, and release
      // the re-entrancy guard so the corrected value can be submitted.
      if (this.pendingInputInFlight) {
        this._pendingInput.set(this.pendingInputInFlight);
        this.pendingInputInFlight = null;
      }
      this._inputValidationError.set(evt.reason);
      this._busy.set(false);
    });
  }

  /**
   * The hub connection is shared by every agent surface, so a store only reacts
   * to events for its own session. This also drops late events from a session
   * the store has already left.
   */
  private forThisSession<T extends SessionEvent>(): MonoTypeOperatorFunction<T> {
    return filter((evt: T) => evt.sessionId === this._sessionId());
  }

  // --- conversation/streaming internals ---

  /** "Talent · Drivkrafter · 20 okt. 08:23", in the user's language. */
  private contextEntry(groupName: string | null, pageName: string | null, createdAt: string): ConversationEntry {
    const time = new Intl.DateTimeFormat(this.transloco.getActiveLang() || undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(createdAt));
    return {
      id: this.nextId++,
      kind: 'context',
      text: [groupName, pageName, time].filter((part) => !!part).join(' · '),
    };
  }

  private renderHistory(messages: AgentMessageDto[]): void {
    this._pendingConfirmation.set(null);
    this._pendingInput.set(null);
    this._inputValidationError.set(null);
    this.streamingIndex = null;
    const entries: ConversationEntry[] = [];

    for (const m of messages) {
      switch (m.role) {
        case 'User':
          entries.push({ id: this.nextId++, kind: 'user', text: m.content ?? '' });
          break;
        case 'Assistant':
          if (m.content) {
            entries.push({ id: this.nextId++, kind: 'assistant', text: m.content });
          }
          break;
        case 'ToolCall': {
          // A ToolCall with no matching ToolResult is a pending confirmation/input
          // we restore so a resumed session can still be answered or approved.
          const hasResult = messages.some(
            (x) => x.role === 'ToolResult' && x.toolCallId === m.toolCallId,
          );
          // RequestFieldValue questions for already-answered calls are shown as
          // assistant messages so they persist in the flow. For an unanswered
          // call, the restored widget renders its own question below, so
          // pushing a bubble here would duplicate it.
          if (m.toolName === 'RequestFieldValue') {
            if (hasResult) {
              const question = this.tryParseInputQuestion(m.toolInput);
              if (question) {
                entries.push({ id: this.nextId++, kind: 'assistant', text: question });
              }
            }
          } else {
            entries.push({
              id: this.nextId++,
              kind: 'tool-call',
              text: `Tool call: ${m.toolName ?? '(unknown)'}`,
              toolName: m.toolName ?? '',
              toolCallId: m.toolCallId ?? '',
              payloadJson: m.toolInput ?? '',
            });
          }
          if (!hasResult && m.toolCallId) {
            if (m.toolName === 'RequestFieldValue') {
              // Prefer the server-resolved request; the raw toolInput may lack
              // dataType. If neither works, restore nothing — the user can type,
              // which cancels the open question. Never show it as a proposal.
              const resumedInput =
                m.inputRequest ?? this.tryParsePendingInput(m.toolCallId, m.toolInput);
              if (resumedInput) {
                this._pendingInput.set(resumedInput);
                this._pendingConfirmation.set(null);
              }
            } else {
              const resumedSummary = this.tryParsePendingConfirmationSummary(
                m.toolName,
                m.toolInput,
              );
              this._pendingConfirmation.set({
                toolCallId: m.toolCallId,
                toolName: m.toolName ?? '',
                proposedPayloadJson: m.toolInput ?? '',
                summary: resumedSummary,
              });
            }
          }
          break;
        }
        case 'ToolResult':
          entries.push({
            id: this.nextId++,
            kind: 'tool-result',
            text: `Result for ${m.toolName ?? '(unknown)'}`,
            toolName: m.toolName ?? '',
            toolCallId: m.toolCallId ?? '',
          });
          break;
        case 'Developer':
          // Context rows (the Coach) are shown as a separator; other Developer rows are not rendered.
          if (m.context) entries.push(this.contextEntry(m.context.groupName, m.context.pageName, m.createdAt));
          break;
      }
    }

    this._messages.set(entries);
  }

  private tryParsePendingConfirmationSummary(
    toolName: string | null,
    toolInput: string | null,
  ): string {
    if (!toolName || !toolInput) {
      return '(resumed pending proposal)';
    }

    if (toolName !== 'ProposeGroupContext') {
      return '(resumed pending proposal)';
    }

    try {
      const parsed = JSON.parse(toolInput) as { summary?: unknown };
      if (typeof parsed.summary === 'string' && parsed.summary.trim().length > 0) {
        return parsed.summary.trim();
      }
    } catch {
      // Fallback?
    }

    return '(resumed pending proposal)';
  }

  private tryParseInputQuestion(toolInput: string | null): string | null {
    if (!toolInput) {
      return null;
    }
    try {
      const parsed = JSON.parse(toolInput) as { question?: unknown };
      return typeof parsed.question === 'string' && parsed.question.trim().length > 0
        ? parsed.question
        : null;
    } catch {
      return null;
    }
  }

  private tryParsePendingInput(
    toolCallId: string,
    toolInput: string | null,
  ): InputRequestedPayload | null {
    if (!toolInput) {
      return null;
    }
    try {
      const parsed = JSON.parse(toolInput) as {
        question?: unknown;
        dataType?: unknown;
        options?: unknown;
        allowFreeText?: unknown;
        allowSkip?: unknown;
      };
      if (typeof parsed.question !== 'string' || typeof parsed.dataType !== 'string') {
        return null;
      }

      const dataType = parsed.dataType as InputRequestedPayload['dataType'];
      if (!['Text', 'Number', 'Bool', 'Date', 'Enum'].includes(dataType)) {
        return null;
      }

      const options = Array.isArray(parsed.options)
        ? parsed.options
            .map((option) => {
              if (!option) return null;
              // Handle string format (from resumed sessions)
              if (typeof option === 'string') {
                return { key: option, label: option };
              }
              // Handle object format (from new agent responses)
              if (typeof option === 'object') {
                const record = option as { key?: unknown; label?: unknown };
                if (typeof record.key !== 'string' || typeof record.label !== 'string') {
                  return null;
                }
                return { key: record.key, label: record.label };
              }
              return null;
            })
            .filter((option): option is { key: string; label: string } => option !== null)
        : [];

      return {
        toolCallId,
        question: parsed.question,
        dataType,
        options,
        allowFreeText: parsed.allowFreeText === true,
        allowSkip: parsed.allowSkip === true,
      };
    } catch {
      return null;
    }
  }

  private push(entry: Omit<ConversationEntry, 'id'>): void {
    this._messages.update((arr) => [...arr, { id: this.nextId++, ...entry }]);
  }

  /**
   * Remove a question the model duplicated at the end of its own message.
   * The widget renders the question itself, so a verbatim copy in the
   * preceding assistant message would show it twice.
   *
   * Deliberately narrow: only the newest entry, only an exact match, and only
   * when the question is the last thing in the message. Anything else is left
   * alone — showing the question twice is a cosmetic problem, deleting text
   * the user needed is not.
   */
  private stripTrailingQuestion(question: string): void {
    const q = question?.trim();
    if (!q) return;

    this._messages.update((arr) => {
      const i = arr.length - 1;
      const entry = arr[i];
      if (!entry || entry.kind !== 'assistant') return arr;

      const idx = entry.text.lastIndexOf(q);
      if (idx === -1) return arr;
      // Must be at the very end, or we'd drop text that follows it.
      if (entry.text.slice(idx + q.length).trim().length > 0) return arr;

      const remainder = entry.text.slice(0, idx).trim();
      const copy = [...arr];
      if (remainder.length === 0) {
        copy.splice(i, 1); // message was nothing but the question
      } else {
        copy[i] = { ...entry, text: remainder }; // keep "Tack." etc.
      }
      return copy;
    });
  }

  private startAssistantEntry(): void {
    this._messages.update((arr) => {
      const next = [
        ...arr,
        { id: this.nextId++, kind: 'assistant' as const, text: '', tokens: [] as string[] },
      ];
      this.streamingIndex = next.length - 1;
      return next;
    });
  }

  private appendToken(token: string): void {
    if (this.streamingIndex === null) {
      this.startAssistantEntry();
    }
    const idx = this.streamingIndex!;
    this._messages.update((arr) => {
      const entry = arr[idx];
      if (!entry) return arr;
      const copy = [...arr];
      copy[idx] = {
        ...entry,
        text: entry.text + token,
        tokens: entry.tokens !== undefined ? [...entry.tokens, token] : undefined,
      };
      return copy;
    });
  }

  private finalizeAssistantEntry(): void {
    const idx = this.streamingIndex;
    if (idx !== null) {
      this._messages.update((arr) => {
        const entry = arr[idx];
        if (entry && entry.kind === 'assistant' && !entry.text) {
          // Drop the empty bubble we speculatively opened.
          const copy = [...arr];
          copy.splice(idx, 1);
          return copy;
        }
        if (entry && entry.tokens !== undefined) {
          // Clear the streaming marker so the thread stops treating this as live.
          const copy = [...arr];
          copy[idx] = { ...entry, tokens: undefined };
          return copy;
        }
        return arr;
      });
    }
    this.streamingIndex = null;
  }

  private discardAssistantEntry(): void {
    const idx = this.streamingIndex;
    if (idx !== null) {
      this._messages.update((arr) => {
        if (arr[idx]?.kind !== 'assistant') return arr;
        const copy = [...arr];
        copy.splice(idx, 1);
        return copy;
      });
    }
    this.streamingIndex = null;
  }

  private errorString(err: unknown): string {
    if (err instanceof Error) return err.message;
    return typeof err === 'string' ? err : JSON.stringify(err);
  }
}
